/**
 * The dashboard rain radar card, shared by both apps: what its pill says and
 * what the card is called. Each app draws these; neither decides them.
 *
 * The card shows ONE frame at the live edge around a place (mobile
 * `RadarNowLite`, webapp `StationDashboard/RainRadar`). Its map is aria-hidden
 * inside one control, so `label` is all a screen reader hears, and the pill is
 * the only on-screen difference between a dry day (frames, no echoes) and a
 * feed outage (no frames): both show an empty basemap.
 */
import {messages} from '@smartmixin/i18n';
import {MessageDescriptor} from 'react-intl';
import type {RainRadarStatus} from '../hooks/useRainRadar';
import {DateTimeConfig, formatDate, hourFormatter} from './Dates';

/** Degrees of latitude and longitude the card spans around the place. */
export const RADAR_CARD_SPAN_DEG = 4;

type FormatMessage = (
  descriptor: MessageDescriptor,
  values?: Record<string, string>,
) => string;

export type RadarCardPill =
  {kind: 'time'; text: string} | {kind: 'unavailable'; text: string};

export type RadarCardView = {
  /** The frame time in the user's clock, or null when there is no frame. */
  time: string | null;
  /** Over the map; null when there is nothing to say (loading, no map). */
  pill: RadarCardPill | null;
  /** The card control's accessible name. */
  label: string;
};

export const radarCardView = ({
  status,
  currentTimestamp,
  mapFailed,
  timezone,
  dateTimeConfig,
  formatMessage,
}: {
  status: RainRadarStatus;
  currentTimestamp: number | undefined;
  /** The basemap could not be drawn (style or WebGL failure). */
  mapFailed: boolean;
  /** The place's timezone: the frame is dated in local time there. */
  timezone: string;
  dateTimeConfig: DateTimeConfig;
  formatMessage: FormatMessage;
}): RadarCardView => {
  // No map: nothing to date or to call unavailable, the map is.
  if (mapFailed) {
    return {
      time: null,
      pill: null,
      label: formatMessage(messages.common.mapUnavailable),
    };
  }
  if (status === 'unavailable') {
    const text = formatMessage(messages.weatherMap.radarUnavailable);
    return {time: null, pill: {kind: 'unavailable', text}, label: text};
  }
  if (currentTimestamp) {
    const time = formatDate(
      currentTimestamp,
      timezone,
      hourFormatter(dateTimeConfig),
    );
    return {
      time,
      pill: {kind: 'time', text: time},
      label: formatMessage(messages.weatherMap.radarCardLabel, {time}),
    };
  }
  return {
    time: null,
    pill: null,
    label: formatMessage(messages.weatherMap.rainRadar),
  };
};
